import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

export const appTranslationRef = createTranslationRef({
  id: 'app',
  messages: {
    // Home
    home: {
      tagline: 'Your developer portal, one search away',
      searchPlaceholder: 'Search across everything...',
      starred: {
        title: 'Favorites',
        emptyTitle: 'No favorites yet',
        emptyDescription: 'Star entities from the catalog to see them here.',
      },
      guide: {
        title: 'User Guide',
        description:
          'Everything you need to register your project, write docs, connect databases, and read quality metrics.',
        action: 'Read the guide',
      },
    },
    // Sidebar
    sidebar: {
      home: 'Home',
      search: 'Search',
      menu: 'Menu',
      catalog: 'Catalog',
      docs: 'Docs',
      create: 'Create...',
      clients: 'Clients',
      techRadar: 'Tech Radar',
      guide: 'User Guide',
      settings: 'Settings',
    },
    // Catalog page
    catalog: {
      pageTitle: 'Catalog',
      filters: {
        all: 'All',
        kind: 'Kind',
        type: 'Type',
        owner: 'Owner',
        searchPlaceholder: 'Search...',
      },
      table: {
        name: 'Name',
        kind: 'Kind',
        type: 'Type',
        owner: 'Owner',
        lifecycle: 'Lifecycle',
        description: 'Description',
        result: 'result',
        results: 'results',
      },
      pagination: {
        show: 'Show',
        results: 'results',
        items: 'items',
        of: 'of',
      },
      emptyState: {
        title: 'No entities found',
        description: 'Try adjusting your filters or search terms',
        noDisplay: 'No entities to display',
      },
    },
    // Clients page
    clients: {
      pageTitle: 'Clients',
      createButton: 'Create Client',
      filters: {
        all: 'All',
        owner: 'Owner',
        searchPlaceholder: 'Search...',
      },
      table: {
        name: 'Name',
        owner: 'Owner',
        description: 'Description',
        result: 'result',
        results: 'results',
      },
      pagination: {
        show: 'Show',
        results: 'results',
        items: 'items',
        of: 'of',
      },
      emptyState: {
        title: 'No clients found',
        description: 'Try adjusting your filters or search terms',
        noDisplay: 'No clients to display',
      },
    },
    // Entity layouts
    entity: {
      tabs: {
        overview: 'Overview',
        general: 'General',
        environments: 'Environments',
        docs: 'Docs',
        cicd: 'CI/CD',
        api: 'API',
        dependencies: 'Dependencies',
        definition: 'Definition',
        diagram: 'Diagram',
        database: 'Database',
        quality: 'Quality',
      },
      cicdEmptyState: {
        title: 'CI/CD via GitLab',
        description:
          'Your projects use GitLab CI/CD. Check your .gitlab-ci.yml file and pipeline status directly in GitLab.',
        viewGitlab: 'View GitLab Projects',
      },
      card: {
        noDescription: 'No description available',
      },
      systemDiagram: 'Solution Diagram',
      componentDiagram: 'Component Diagram',
    },
    // Quality Center page
    quality: {
      pageTitle: 'Quality Center',
      tabs: {
        overview: 'Overview',
        issues: 'Issues Explorer',
      },
      noData: 'No quality data available for this component.',
      noSummaryData: 'No summary data',
      noFileMetrics: 'No file metrics available',
      heatmap: {
        back: 'Back',
        root: 'root',
        density: 'Density',
        complexity: 'Complexity',
        legendLow: 'low',
        legendHigh: 'high',
        folders: 'Folders',
        files: 'Files',
        loc: 'LOC',
        issue: 'issue',
        issues: 'issues',
        clickToDrill: 'click to drill down',
        clickToIssues: 'click \u2192 Issues Explorer',
        tooltip: {
          dir: 'DIR',
          file: 'FILE',
          path: 'path',
          issues: 'Issues',
          blocking: 'Blocking',
          complexitySection: 'COMPLEXITY',
          cyclomatic: 'Cyclomatic',
          cognitive: 'Cognitive',
          avgPerFn: 'avg/fn',
          lcom4: 'LCOM4',
          noIssues: 'No issues',
          files: 'files',
          functions: 'fn',
        },
      },
      health: {
        keyMetrics: 'Key Metrics',
        totalIssues: 'Total Issues',
        linesOfCode: 'Lines of Code',
        complexity: 'Complexity',
        affectedFiles: 'Affected Files',
        blocking: 'blocking',
        files: 'files',
        aboveThreshold: 'above threshold',
        outOf: 'out of',
        healthScore: 'HEALTH SCORE',
        qualityGates: 'Quality Gates',
        pass: 'PASS',
        warn: 'WARN',
        fail: 'FAIL',
        byTool: 'By Tool',
      },
      issues: {
        searchPlaceholder: 'Search issues...',
        filterLevel: 'Level',
        filterMode: 'Mode',
        filterTool: 'Tool',
        filterCategory: 'Category',
        allLevels: 'All levels',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
        allModes: 'All modes',
        block: 'Block',
        comment: 'Comment',
        issueCount: 'issues',
        byFile: 'By file',
        flat: 'Flat',
        noMatchSearch: 'No issues match your search.',
        noIssuesFound: 'No issues found.',
        loadMore: 'Load more',
      },
      summary: {
        noData: 'No quality data available',
        scoreGood: 'Good',
        scoreFair: 'Fair',
        scorePoor: 'Poor',
        issues: 'Issues',
        blocking: 'Blocking',
        cognitiveAvg: 'Cognitive avg',
        gatesPassed: 'gates passed',
        openQualityCenter: 'Open Quality Center',
      },
      issueCard: {
        high: 'HIGH',
        med: 'MED',
        low: 'LOW',
        block: 'BLOCK',
        comment: 'COMMENT',
        docs: 'docs',
      },
    },
    // Docs page
    docs: {
      pageTitle: 'Documentation',
      contentTitle: 'Technical Documentation',
      searchPlaceholder: 'Search documentation...',
      entityCountSingular: '1 documentation set',
      entityCountPlural: '{{count}} documentation sets',
      emptyState: {
        title: 'No documentation found',
        description: 'No entities with TechDocs are available yet.',
      },
    },
    // Create page
    create: {
      pageTitle: 'Create',
      contentTitle: 'What do you want to create?',
      tiles: {
        client: {
          title: 'Client',
          description: 'Register a new client organization in the catalog.',
        },
        application: {
          title: 'Application',
          description: 'Scaffold a new application or service.',
        },
        component: {
          title: 'Component',
          description: 'Add a reusable component to the catalog.',
        },
      },
    },
    // Auth
    auth: {
      welcome: 'Welcome',
      subtitle: 'Sign in to access the developer portal',
      loginFailed: 'Sign-in failed. Please try again.',
      gitlab: 'GitLab',
      signInWithGitlab: 'Sign in with GitLab',
      microsoft: 'Microsoft',
      signInWithMicrosoft: 'Sign in with Microsoft',
      guest: 'Guest',
      continueAsGuest: 'Continue as Guest',
      or: 'or',
    },
    // About card
    about: {
      title: 'About',
      refresh: 'Refresh entity',
      viewSource: 'View Source',
      viewDocs: 'Documentation',
      owner: 'Owner',
      system: 'Solution',
      domain: 'Client',
      tags: 'Tags',
    },
    // Links card
    links: {
      title: 'Links',
      empty: 'No links available',
    },
    // Relations card / graph
    relations: {
      title: 'Relations',
      empty: 'No relations found',
      graph: {
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        fitView: 'Fit view',
        currentEntity: 'Current entity',
      },
      labels: {
        ownedBy: 'Owner',
        partOf: 'Part of',
        hasPart: 'Has parts',
        providesApi: 'Provides API',
        consumesApi: 'Consumes API',
        apiProvidedBy: 'Provided by',
        apiConsumedBy: 'Consumed by',
        dependsOn: 'Depends on',
        dependencyOf: 'Dependency of',
        parentOf: 'Parent of',
        childOf: 'Child of',
        memberOf: 'Member of',
        hasMember: 'Members',
        ownerOf: 'Owns',
      },
    },
    // Has Systems card
    hasSystems: {
      title: 'Solutions',
      empty: 'No solutions found',
      error: 'Failed to load solutions',
    },
    // Has Components card
    hasComponents: {
      title: 'Components',
      empty: 'No components found',
      error: 'Failed to load components',
    },
    // Has APIs card
    hasApis: {
      title: 'APIs',
      empty: 'No APIs found',
      error: 'Failed to load APIs',
    },
    // Has Resources card
    hasResources: {
      title: 'Resources',
      empty: 'No resources found',
      error: 'Failed to load resources',
    },
    // Provided APIs card
    providedApis: {
      title: 'Provided APIs',
      empty: 'No provided APIs',
      error: 'Failed to load provided APIs',
    },
    // Consumed APIs card
    consumedApis: {
      title: 'Consumed APIs',
      empty: 'No consumed APIs',
      error: 'Failed to load consumed APIs',
    },
    // Providing Components card
    providingComponents: {
      title: 'Providing Components',
      empty: 'No providing components',
      error: 'Failed to load providing components',
    },
    // Consuming Components card
    consumingComponents: {
      title: 'Consuming Components',
      empty: 'No consuming components',
      error: 'Failed to load consuming components',
    },
    // Group Profile card
    groupProfile: {
      refresh: 'Refresh group',
      type: 'Type',
      email: 'Email',
      parent: 'Parent Group',
      children: 'Child Groups',
    },
    // User Profile card
    userProfile: {
      refresh: 'Refresh user',
      email: 'Email',
      memberOf: 'Member Of',
      groups: 'Groups',
    },
    // Ownership card
    ownership: {
      title: 'Ownership',
      empty: 'No owned entities',
      error: 'Failed to load ownership',
    },
    // Members card
    members: {
      title: 'Members',
      empty: 'No members found',
      error: 'Failed to load members',
    },
    // GitLab enrichment cards
    // Relative time
    relativeTime: {
      today: 'today',
      yesterday: 'yesterday',
      daysAgo: '{{count}} days ago',
      weeksAgo: '{{count}} weeks ago',
      monthsAgo: '{{count}} months ago',
      yearsAgo: '{{count}} years ago',
    },
    gitlab: {
      languages: {
        title: 'Languages',
        emptyState: {
          title: 'No languages detected',
          description:
            'This project has no language data available from GitLab.',
        },
      },
      releases: {
        title: 'Recent Releases',
        release: 'release',
        releases: 'releases',
        emptyState: {
          title: 'No releases found',
          description: 'This project has no GitLab releases published yet.',
        },
      },
      environments: {
        title: 'Environments',
        environment: 'environment',
        environments: 'environments',
        emptyState: {
          title: 'No active environments',
          description: 'This project has no active GitLab environments.',
        },
      },
    },
    // Search
    search: {
      resultType: 'Result Type',
      softwareCatalog: 'Software Catalog',
      searchPlaceholder: 'Search...',
      noResults: 'No results found',
      noResultsDescription: 'Try adjusting your search term or filters',
      viewAllResults: 'View all results',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      filters: {
        kind: 'Kind',
        lifecycle: 'Lifecycle',
        component: 'Component',
        template: 'Template',
        experimental: 'Experimental',
        production: 'Production',
      },
      modal: {
        placeholder: 'Search across everything...',
      },
    },
    // Settings
    settings: {
      pageTitle: 'Settings',
      profile: {
        title: 'Profile',
        email: 'Email',
        displayName: 'Display Name',
      },
      appearance: {
        title: 'Appearance',
        theme: 'Theme',
        language: 'Language',
      },
      auth: {
        title: 'Authentication',
        signOut: 'Sign Out',
        signOutDescription: 'End your current session',
        provider: 'Provider',
      },
    },
    // API Definition
    apiDefinition: {
      unsupported: 'Unsupported API type',
    },
    // Entity Layout
    entityLayout: {
      notFound: 'Entity not found',
      favorite: 'Toggle favorite',
      contextMenu: {
        more: 'More',
        copyUrl: 'Copy entity URL',
        copied: 'Copied!',
        inspect: 'Inspect entity',
        unregister: 'Unregister entity',
      },
    },
    // DB Explorer
    dbExplorer: {
      emptyState: {
        title: 'No database environment',
        description:
          'This resource has no linked component with configured environments.',
      },
      envSelector: {
        label: 'Environment',
      },
      sqlEditor: {
        title: 'SQL Query',
        run: 'Run',
        running: 'Running...',
        placeholder: 'SELECT * FROM users LIMIT 10;',
      },
      results: {
        title: 'Results',
        show: 'Show',
        row: 'row',
        rows: 'rows',
        truncated: '(truncated)',
        loading: 'Executing query...',
        noColumns: 'Query returned no columns.',
      },
      unsupportedHosting: {
        title: 'Database explorer unavailable',
        description: 'This database is hosted on {{hosting}}. The explorer currently only supports Kubernetes-hosted databases.',
      },
      schema: {
        title: 'Database Schema',
        loading: 'Loading schema...',
        empty: 'No tables found in this database.',
        pending: 'Schema will appear after the next catalog refresh.',
        tables: 'tables',
        table: 'table',
        relations: 'relations',
        relation: 'relation',
      },
    },
    // Error display
    error: {
      title: 'Something went wrong',
      retry: 'Retry',
    },
    // Not found
    notFound: {
      title: 'Lost in space',
      description:
        "The page you're looking for has drifted into the void. It may have been moved or doesn't exist.",
      action: 'Go home',
      goBack: 'Go back',
    },
    // Entity Inspector
    entityInspector: {
      title: 'Entity Inspector',
      close: 'Close',
      tabs: {
        overview: 'Overview',
        ancestry: 'Ancestry',
        colocated: 'Colocated',
        json: 'Raw JSON',
        yaml: 'Raw YAML',
      },
      copyToClipboard: 'Copy to clipboard',
      copied: 'Copied!',
      metadata: 'Metadata',
      name: 'Name',
      namespace: 'Namespace',
      kind: 'Kind',
      labels: 'Labels',
      annotations: 'Annotations',
      relations: 'Relations',
      noLabels: 'No labels',
      noAnnotations: 'No annotations',
      noRelations: 'No relations',
    },
  },
});
